import { cascadingPicks } from '@alison/api/preflight';
import { FormFieldLabel, networkIconFor } from '@alison/ui';
import AddOutlined from '@mui/icons-material/AddOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { usePlatformFields, usePrefetchPlatformFields } from '../../lib/workflows.hooks.js';
import { CampaignField } from './CampaignField.js';
import { CreativeName } from './CreativeName.js';
import { FieldTooltip } from './FieldHeader.js';
import type { Platform } from './networks.types.js';
import {
  applyToAll,
  type PreflightConfig,
  SCORING_MODES,
  type ScoringMode,
  setPlatforms,
  setScoringMode,
  setValue,
  valuesFor,
} from './preflightConfig.js';
import { SegmentedControl } from './SegmentedControl.js';

const LABEL_SX = { color: 'text.disabled', fontSize: 10 };
const RAIL_WIDTH = 232;
const FIELD_SKELETON_KEYS = ['a', 'b', 'c', 'd'];
const RAIL_MAX_HEIGHT = 420;

const SCORE_HINT =
  'Historical performance scores against this account’s own results, best practice against the Creative Genome, and Both blends them.';
const PLATFORM_HINT =
  'Each creative is scored once per platform you pick, against that platform’s own rules.';

const PLATFORM_SX = {
  minWidth: 44,
  height: 36,
  px: 0,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
};

const PLATFORM_ON_SX = { borderColor: 'text.primary', bgcolor: 'action.selected' };

const CREATIVE_SX = {
  px: 2.5,
  py: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'block',
  width: '100%',
};

const CREATIVE_ON_SX = { borderColor: 'text.primary' };

const APPLY_SX = {
  borderRadius: 2,
  px: 2.5,
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  flexShrink: 0,
};

const APPLIED_SX = {
  ...APPLY_SX,
  bgcolor: 'success.main',
  borderColor: 'success.main',
  color: 'common.white',
  '&:hover': { bgcolor: 'success.dark', borderColor: 'success.dark' },
};

export interface ConfigureCreative {
  id: string;
  name: string;
  type: 'Video' | 'Banner';
}

export interface Benchmark {
  count: number;
  minimum: number;
  isEnough: boolean;
  isFetching: boolean;
}

interface ConfigureStepProps {
  config: PreflightConfig;
  platforms: Platform[];
  creatives: ConfigureCreative[];
  picked: string;
  platform: string | null;
  benchmark: Benchmark;
  onChange: (config: PreflightConfig) => void;
  onPick: (creativeId: string) => void;
  onPickPlatform: (slug: string) => void;
  onAddMore: () => void;
  onRename: (creativeId: string, name: string) => void;
}

function countOf(creatives: ConfigureCreative[]): string {
  const videos = creatives.filter((creative) => creative.type === 'Video').length;
  const images = creatives.length - videos;
  return [
    videos === 0 ? null : `${videos} video${videos === 1 ? '' : 's'}`,
    images === 0 ? null : `${images} image${images === 1 ? '' : 's'}`,
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

function PlatformButton({
  platform,
  on,
  onToggle,
}: {
  platform: Platform;
  on: boolean;
  onToggle: () => void;
}): ReactNode {
  const Icon = networkIconFor(platform.slug);
  return (
    <Button
      aria-label={platform.label}
      aria-pressed={on}
      onClick={onToggle}
      sx={[PLATFORM_SX, on && PLATFORM_ON_SX]}
    >
      {Icon === undefined ? platform.label : <Icon sx={{ fontSize: 18 }} />}
    </Button>
  );
}

function FieldSkeleton(): ReactNode {
  return (
    <Stack data-testid="field-skeleton" sx={{ gap: 1 }}>
      <Skeleton variant="text" width={110} sx={{ fontSize: 10 }} />
      <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
    </Stack>
  );
}

export function ConfigureStep({
  config,
  platforms,
  creatives,
  picked,
  platform,
  benchmark,
  onChange,
  onPick,
  onPickPlatform,
  onAddMore,
  onRename,
}: ConfigureStepProps): ReactNode {
  const thin =
    config.scoringMode !== 'best_practice' && !benchmark.isEnough && !benchmark.isFetching;
  const prefetchFields = usePrefetchPlatformFields();
  const chosen = platforms.filter((platform) => config.platforms.includes(platform.slug));
  const [appliedFor, setAppliedFor] = useState<{ creative: string; platform: string } | null>(null);
  // The parent owns the open tab: it feeds the benchmark count, and a remount
  // (after a failed start) must not silently fall back to the first platform.
  const open = chosen.find((entry) => entry.slug === platform) ?? chosen[0];
  const entered = open === undefined ? {} : valuesFor(config, picked, open.slug);
  const { fields, isError, isFetching } = usePlatformFields(
    open?.slug ?? null,
    cascadingPicks(entered),
  );
  const loading = isFetching && fields.length === 0;
  const typed = fields.filter((field) => field.kind === 'text');
  const chosenFields = fields.filter((field) => field.kind !== 'text');
  const applied = appliedFor?.creative === picked && appliedFor?.platform === open?.slug;

  const setField = (field: string, next: string): void => {
    setAppliedFor(null);
    onChange(setValue(config, picked, open?.slug ?? '', field, next));
  };

  const togglePlatform = (slug: string): void => {
    const on = config.platforms.includes(slug);
    if (!on) prefetchFields(slug);
    onChange(
      setPlatforms(
        config,
        platforms
          .map((platform) => platform.slug)
          .filter((each) => (each === slug ? !on : config.platforms.includes(each))),
      ),
    );
  };

  return (
    <Stack sx={{ gap: 5 }}>
      <Stack direction="row" sx={{ gap: 8, flexWrap: 'wrap' }}>
        <Stack sx={{ gap: 1.5 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <FormFieldLabel sx={LABEL_SX}>BASE SCORE ON</FormFieldLabel>
            <FieldTooltip tooltip={SCORE_HINT} />
          </Stack>
          <SegmentedControl<ScoringMode>
            label="Base score on"
            options={SCORING_MODES}
            value={config.scoringMode}
            onChange={(mode) => onChange(setScoringMode(config, mode))}
          />
          {thin ? (
            <Alert severity="warning" icon={<WarningAmberOutlined sx={{ fontSize: 20 }} />}>
              We need a little more data to score your creatives on performance — {benchmark.count}{' '}
              of {benchmark.minimum} creatives in this date range. Consider widening the date range,
              or scoring on best practice alone.
            </Alert>
          ) : null}
        </Stack>

        <Stack sx={{ gap: 1.5 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <FormFieldLabel sx={LABEL_SX}>PLATFORMS</FormFieldLabel>
            <FieldTooltip tooltip={PLATFORM_HINT} />
          </Stack>
          <Stack direction="row" sx={{ gap: 1 }}>
            {platforms.map((platform) => (
              <PlatformButton
                key={platform.slug}
                platform={platform}
                on={config.platforms.includes(platform.slug)}
                onToggle={() => togglePlatform(platform.slug)}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ gap: 6, alignItems: 'flex-start' }}>
        <Stack sx={{ gap: 2, width: RAIL_WIDTH, flexShrink: 0 }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {creatives.length} creatives uploaded
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {countOf(creatives)}
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<AddOutlined />}
              onClick={onAddMore}
              sx={{ color: 'primary.main', flexShrink: 0 }}
            >
              Add
            </Button>
          </Stack>
          <Stack sx={{ gap: 2, overflowY: 'auto', maxHeight: RAIL_MAX_HEIGHT, pr: 0.5 }}>
            {creatives.map((creative) => (
              <Box
                key={creative.id}
                component="button"
                type="button"
                onClick={() => onPick(creative.id)}
                sx={[CREATIVE_SX, creative.id === picked && CREATIVE_ON_SX]}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                  {creative.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {creative.type === 'Video' ? 'Video' : 'Image'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>

        <Stack sx={{ gap: 3, flex: 1, minWidth: 0 }}>
          <CreativeName
            key={picked}
            name={creatives.find((creative) => creative.id === picked)?.name ?? ''}
            variant="h3"
            nameSx={{ fontSize: 16 }}
            onRename={(name) => onRename(picked, name)}
          />

          {open === undefined ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Pick at least one platform to say what these creatives are for.
            </Typography>
          ) : (
            <>
              <Stack
                direction="row"
                sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Tabs
                  value={open.slug}
                  onChange={(_event, slug: string) => onPickPlatform(slug)}
                  sx={{ minHeight: 36 }}
                >
                  {chosen.map((platform) => (
                    <Tab
                      key={platform.slug}
                      value={platform.slug}
                      label={platform.label}
                      sx={{ minHeight: 36, fontSize: 13 }}
                    />
                  ))}
                </Tabs>
                {creatives.length > 1 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={applied ? <CheckOutlined sx={{ fontSize: 15 }} /> : undefined}
                    sx={applied ? APPLIED_SX : APPLY_SX}
                    onClick={() => {
                      onChange(
                        applyToAll(
                          config,
                          picked,
                          open.slug,
                          creatives.map((creative) => creative.id),
                        ),
                      );
                      setAppliedFor({ creative: picked, platform: open.slug });
                    }}
                  >
                    {applied ? 'Applied' : `Apply ${open.label} to all creatives`}
                  </Button>
                ) : null}
              </Stack>

              {isError ? (
                <Alert severity="error">
                  These fields could not be loaded. The run can still be started without them.
                </Alert>
              ) : null}

              {typed.map((field) => (
                <CampaignField
                  key={field.key}
                  field={field}
                  value={entered[field.key] ?? ''}
                  busy={isFetching}
                  onSet={(next) => setField(field.key, next)}
                />
              ))}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 4,
                  rowGap: 3,
                }}
              >
                {loading ? FIELD_SKELETON_KEYS.map((key) => <FieldSkeleton key={key} />) : null}
                {chosenFields.map((field) => (
                  <CampaignField
                    key={field.key}
                    field={field}
                    value={entered[field.key] ?? ''}
                    busy={isFetching}
                    onSet={(next) => setField(field.key, next)}
                  />
                ))}
              </Box>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
